import React, { useEffect, useState, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import './CompanyHistory.css';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// 자료실 컴포넌트
const DownloadCenter = ({ activeTab }) => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const postsPerPage = 5;

  // Firebase에서 게시글 실시간 로드
  useEffect(() => {
    const postsRef = collection(db, 'ko-posts');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    });

    // 컴포넌트 언마운트 시 구독 해제
    return () => unsubscribe();
  }, []);

  // 탭이 바뀔 때 검색어 초기화
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, [activeTab]);

  // 탭에 따른 데이터 필터링
  const filteredPosts = posts.filter(post => {
    // 카테고리 필터링
    const categoryMatch = activeTab === 'catalog' ? 
      (post.category === 'catalog' || !post.category) : 
      (post.category === 'technical');
    
    // 검색어 필터링
    const searchMatch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       post.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    return categoryMatch && searchMatch;
  });

  // 페이지네이션
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // PDF 다운로드
  const handleDownload = (post) => {
    if (post.pdfUrl) {
      window.open(post.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (post.pdfFile) {
      const link = document.createElement('a');
      link.href = post.pdfFile;
      link.download = post.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 게시글 클릭 시 팝업 표시 및 조회수 증가
  const handlePostClick = async (post) => {
    setSelectedPost(post);
    setShowPopup(true);
    
    // 조회수 증가
    try {
      const { doc, updateDoc, increment } = await import('firebase/firestore');
      const postRef = doc(db, 'ko-posts', post.id);
      await updateDoc(postRef, {
        viewCount: increment(1)
      });
    } catch (error) {
      console.error('조회수 증가 실패:', error);
    }
  };

  // 팝업 닫기
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPost(null);
  };

  // 페이지 변경
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getCenterInfo = () => {
    if (activeTab === 'catalog') {
      return {
        icon: '📋',
        title: '카탈로그 보기',
        subtitle: '태봉산업기술(주)의 제품 카탈로그입니다.'
      };
    } else {
      return {
        icon: '📚',
        title: '기술 자료실',
        subtitle: '태봉산업기술(주)의 기술 문서 및 매뉴얼입니다.'
      };
    }
  };

  const centerInfo = getCenterInfo();

  return (
    <div className="board-container">
      {/* 게시판 헤더 */}
      <div className="board-header">
        <h2>{centerInfo.icon} {centerInfo.title}</h2>
        <p>{centerInfo.subtitle}</p>
      </div>

      {/* 게시글 목록 */}
      <div className="board-content">
        <div className="board-table">
          <table>
            <thead>
              <tr>
                <th className="col-number">번호</th>
                <th className="col-title">제목</th>
                <th className="col-author">작성자</th>
                <th className="col-date">작성일</th>
                <th className="col-views">조회수</th>
              </tr>
            </thead>
            <tbody>
              {currentPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-message">
                    <div className="empty-state">
                      <span>📄</span>
                      <p>등록된 자료가 없습니다.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentPosts.map((post, index) => (
                  <tr key={post.id} onClick={() => handlePostClick(post)} className="post-row">
                    <td className="post-number">{startIndex + index + 1}</td>
                    <td className="post-title">
                      <span className="title-text">{post.title?.replace(/[^\w\s가-힣]/g, '') || '제목 없음'}</span>
                    </td>
                    <td className="post-author">관리자</td>
                    <td className="post-date">
                      {post.createdAt ? 
                        new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString('ko-KR') : 
                        '날짜 없음'
                      }
                    </td>
                    <td className="post-views">{post.viewCount || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="board-pagination">
            <div className="pagination-info">
              총 {filteredPosts.length}개의 자료
            </div>
            <div className="pagination-buttons">
              <button 
                className="page-btn prev-btn"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                이전
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const page = startPage + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button 
                className="page-btn next-btn"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 검색 영역 */}
      <div className="board-search">
        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="자료 제목이나 내용을 검색하세요..."
            className="search-input"
          />
          <button className="search-btn">
            <span>🔍</span>
            검색
          </button>
        </div>
      </div>

      {/* 게시글 상세 팝업 */}
      {showPopup && selectedPost && (
        <div className="post-popup-overlay" onClick={handleClosePopup}>
          <div className="post-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{selectedPost.title?.replace(/[^\w\s가-힣]/g, '') || '제목 없음'}</h3>
              <button className="close-btn" onClick={handleClosePopup}>
                ✕
              </button>
            </div>
            <div className="popup-content">
              <div className="post-meta">
                <span className="meta-item">
                  <strong>작성자:</strong> 관리자
                </span>
                <span className="meta-item">
                  <strong>작성일:</strong> {
                    selectedPost.createdAt ? 
                      new Date(selectedPost.createdAt.seconds ? selectedPost.createdAt.seconds * 1000 : selectedPost.createdAt).toLocaleDateString('ko-KR') : 
                      '날짜 없음'
                  }
                </span>
                <span className="meta-item">
                  <strong>조회수:</strong> {selectedPost.viewCount || 0}
                </span>
              </div>
              <div className="post-body">
                <p>{selectedPost.content}</p>
              </div>
              {selectedPost.pdfFileName && (
                <div className="popup-actions">
                  <button 
                    onClick={() => handleDownload(selectedPost)}
                    className="download-btn"
                  >
                    📄 {selectedPost.pdfFileName} 다운로드
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerSupport = () => {
  // URL 해시가 있으면 해당 섹션을 기본값으로 설정
  const getInitialSection = () => {
    const hash = window.location.hash.replace('#', '');
    return hash && ['customer-center', 'online-quote', 'download-center'].includes(hash) 
      ? hash 
      : 'customer-center';
  };
  
  const [activeSection, setActiveSection] = useState(getInitialSection);
  const [activeTab, setActiveTab] = useState('catalog');
  
  const sidebarItems = [
    { name: '고객센터', path: 'customer-center' },
    { name: '온라인견적', path: 'online-quote' },
    { name: '자료실', path: 'download-center' }
  ];

  // 스크롤 스파이 기능
  useEffect(() => {
    const handleScroll = () => {
      const sections = sidebarItems.map(item => item.path);
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 해시 URL 처리 (페이지 로드 시 해당 섹션으로 이동)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['customer-center', 'online-quote', 'download-center'].includes(hash)) {
      setActiveSection(hash);
      // 여러 번 시도하여 확실하게 처리
      const attemptScroll = (attempts = 0) => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 5) {
          setTimeout(() => attemptScroll(attempts + 1), 200);
        }
      };
      setTimeout(() => attemptScroll(), 100);
    }
  }, []);

  // 페이지 로드 완료 후 해시 처리 (추가 보장)
  useEffect(() => {
    const handleLoad = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['customer-center', 'online-quote', 'download-center'].includes(hash)) {
        setActiveSection(hash);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'auto' });
    }
  };

  // 견적 요청 폼 제출 처리
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // 이메일 주소 조합
    const email = `${data.emailUser}@${data.emailDomain}`;
    
    // 서버로 데이터 전송
    try {
      // 환경에 따른 API URL 설정
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://us-central1-taibong-hp.cloudfunctions.net/sendQuote'  // Firebase Functions URL
        : 'http://localhost:5000/api/send-quote';  // 로컬 개발 서버
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company: data.company,
          name: data.name,
          telephone: data.telephone,
          projectName: data.projectName,
          serviceInProcess: data.serviceInProcess,
          email: email,
          hotFluidName: data.hotFluidName,
          coldFluidName: data.coldFluidName,
          hotFlowRate: data.hotFlowRate,
          coldFlowRate: data.coldFlowRate,
          flowUnit: data.flowUnit,
          hotTempIn: data.hotTempIn,
          coldTempIn: data.coldTempIn,
          tempUnit: data.tempUnit,
          hotTempOut: data.hotTempOut,
          coldTempOut: data.coldTempOut,
          tempOutUnit: data.tempOutUnit,
          hotPressureDrop: data.hotPressureDrop,
          coldPressureDrop: data.coldPressureDrop,
          pressureUnit: data.pressureUnit,
          hotDesignPressure: data.hotDesignPressure,
          coldDesignPressure: data.coldDesignPressure,
          designPressureUnit: data.designPressureUnit,
          hotDesignTemp: data.hotDesignTemp,
          coldDesignTemp: data.coldDesignTemp,
          designTempUnit: data.designTempUnit,
          plateMaterial: data.plateMaterial,
          gasketMaterial: data.gasketMaterial,
          comment: data.comment
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ 견적 요청서가 saledept@taibong.com으로 전송되었습니다!\n\n회신을 받으실 수 있도록 이메일 주소를 확인해주세요.');
        e.target.reset();
      } else {
        alert(`❌ 견적 요청서 전송에 실패했습니다: ${result.message}`);
      }
    } catch (error) {
      console.error('견적 요청서 전송 오류:', error);
      alert('❌ 서버 연결 오류: 서버가 실행 중인지 확인해주세요.\n\n서버 실행 방법:\n1. cd new_project/server\n2. npm start');
    }
  };

  return (
    <>
      <Sidebar 
        category="고객지원" 
        items={sidebarItems} 
        activeSection={activeSection}
        onItemClick={scrollToSection}
      />
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">고객지원</h1>
          
          {/* 고객센터 섹션 */}
          <section id="customer-center" className="section">
            <h2 className="section-title">고객센터</h2>
            <div className="section-container">
              <div className="contact-offices">
                {/* 인천 본사 */}
                <div className="office-card">
                  <h3 className="office-title">인천 본사</h3>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-label">주소:</span>
                      <span className="contact-value">인천광역시 남동구 남동동로 327 (남동공단 43B-9L)</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Tel:</span>
                      <span className="contact-value">032-811-0511</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Email:</span>
                      <span className="contact-value">saledept@taibong.com</span>
                    </div>
                  </div>
                  <div className="office-image-container">
                    <img src="/com_incheon.png" alt="인천 본사" className="office-image" />
                  </div>
                </div>

                {/* 남부사업소 */}
                <div className="office-card">
                  <h3 className="office-title">남부사업소</h3>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-label">주소:</span>
                      <span className="contact-value">포항시 흥해읍 해안로 968, 엔솔 1층</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Tel:</span>
                      <span className="contact-value">054-231-0513</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Email:</span>
                      <span className="contact-value">shlee1@taibong.com</span>
                    </div>
                  </div>
                  <div className="office-image-container">
                    <img src="/com_pohang.png" alt="남부사업소" className="office-image" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 온라인견적 섹션 */}
          <section id="online-quote" className="section">
            <h2 className="section-title">온라인견적</h2>
            <div className="section-container">
              <div className="quote-form-container">
                <div className="form-header">
                  <div className="form-icon">→</div>
                  <h3 className="form-title">QUESTIONNAIRE for PLATE HEAT EXCHANGER</h3>
                </div>
                
                <form className="quote-form" onSubmit={handleQuoteSubmit}>
                  {/* 회사 및 연락처 정보 */}
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Company</label>
                        <input type="text" name="company" className="field-input" required />
                      </div>
                      <div className="form-field">
                        <label className="field-label">Name</label>
                        <input type="text" name="name" className="field-input" required />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Telephone</label>
                        <input type="tel" name="telephone" className="field-input" required />
                      </div>
                      <div className="form-field">
                        <label className="field-label">Project Name</label>
                        <input type="text" name="projectName" className="field-input" required />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Service in Process</label>
                        <input type="text" name="serviceInProcess" className="field-input" required />
                      </div>
                      <div className="form-field email-field">
                        <label className="field-label">E-mail</label>
                        <div className="email-inputs">
                          <input type="text" name="emailUser" className="email-input" required />
                          <span className="email-at">@</span>
                          <input type="text" name="emailDomain" className="email-input" required />
                          <select name="emailDomainSelect" className="email-domain-select" onChange={(e) => {
                            const domainInput = e.target.parentElement.querySelector('input[name="emailDomain"]');
                            if (e.target.value && e.target.value !== '') {
                              domainInput.value = e.target.value;
                            } else {
                              domainInput.value = '';
                            }
                          }}>
                            <option value="">직접입력</option>
                            <option value="gmail.com">gmail.com</option>
                            <option value="naver.com">naver.com</option>
                            <option value="daum.net">daum.net</option>
                            <option value="nate.com">nate.com</option>
                            <option value="yahoo.com">yahoo.com</option>
                            <option value="hotmail.com">hotmail.com</option>
                            <option value="outlook.com">outlook.com</option>
                            <option value="hanmail.net">hanmail.net</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 유체 사양 */}
                  <div className="form-section">
                    <h4 className="section-subtitle">상세 (입력 가능시)</h4>
                    <div className="spec-table">
                      <div className="table-header">
                        <div className="table-cell header-cell"></div>
                        <div className="table-cell header-cell">HOT FLUID</div>
                        <div className="table-cell header-cell">COLD FLUID</div>
                        <div className="table-cell header-cell">UNIT</div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Fluid Name</div>
                        <div className="table-cell"><input type="text" name="hotFluidName" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldFluidName" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="fluidUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Flow Rate(Kg/Hr)</div>
                        <div className="table-cell"><input type="text" name="hotFlowRate" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldFlowRate" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="flowUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Temperature(In)(°C)</div>
                        <div className="table-cell"><input type="text" name="hotTempIn" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldTempIn" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="tempUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Temperature(Out)(°C)</div>
                        <div className="table-cell"><input type="text" name="hotTempOut" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldTempOut" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="tempOutUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Pressure Drop(Kg/cm²)</div>
                        <div className="table-cell"><input type="text" name="hotPressureDrop" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldPressureDrop" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="pressureUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Design Pressure(Kg/cm²)</div>
                        <div className="table-cell"><input type="text" name="hotDesignPressure" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldDesignPressure" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="designPressureUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Design Temperature(°C)</div>
                        <div className="table-cell"><input type="text" name="hotDesignTemp" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldDesignTemp" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="designTempUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Plate Material</div>
                        <div className="table-cell">
                          <select name="plateMaterial" className="table-select">
                            <option value="">-- Select --</option>
                            <option value="SS304">SS304</option>
                            <option value="SS316">SS316</option>
                            <option value="SS316L">SS316L</option>
                            <option value="Hastelloy C">Hastelloy C</option>
                            <option value="Ti">Ti</option>
                            <option value="SM0254">SM0254</option>
                            <option value="904L">904L</option>
                          </select>
                        </div>
                        <div className="table-cell"></div>
                        <div className="table-cell"></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Gasket Material</div>
                        <div className="table-cell">
                          <select name="gasketMaterial" className="table-select">
                            <option value="">-- Select --</option>
                            <option value="NBR">NBR</option>
                            <option value="EPDM">EPDM</option>
                            <option value="VITON">VITON</option>
                            <option value="H-NBR">H-NBR</option>
                            <option value="SILICONE">SILICONE</option>
                            <option value="PTFE">PTFE</option>
                          </select>
                        </div>
                        <div className="table-cell"></div>
                        <div className="table-cell"></div>
                      </div>
                    </div>
                  </div>

                  {/* 코멘트 */}
                  <div className="form-section">
                    <h4 className="section-subtitle">COMMENT</h4>
                    <textarea name="comment" className="comment-textarea" rows="4" placeholder="추가 코멘트를 입력해주세요."></textarea>
                  </div>

                  {/* 제출 버튼 */}
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">견적 요청하기</button>
                  </div>
                </form>
              </div>
            </div>
          </section>

          {/* 자료실 섹션 */}
          <section id="download-center" className="section">
            <h2 className="section-title">자료실</h2>
            <div className="section-container">
              {/* 자료실 버튼 섹션 */}
              <div className="download-center-buttons">
                <button 
                  className={`download-center-btn ${activeTab === 'catalog' ? 'active' : ''}`}
                  onClick={() => setActiveTab('catalog')}
                >
                  <span className="btn-icon">📋</span>
                  <span className="btn-text">카탈로그 보기</span>
                </button>
                <button 
                  className={`download-center-btn ${activeTab === 'technical' ? 'active' : ''}`}
                  onClick={() => setActiveTab('technical')}
                >
                  <span className="btn-icon">📚</span>
                  <span className="btn-text">기술 자료실</span>
                </button>
              </div>
              
              <DownloadCenter activeTab={activeTab} />
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default CustomerSupport;
